import { useEffect, useState } from 'react';

interface HeroSectionProps {
  onCtaClick: () => void;
}

export default function HeroSection({ onCtaClick }: HeroSectionProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled ? 'bg-black/95 shadow-lg shadow-yellow-900/20 py-2' : 'bg-transparent py-3 md:py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 md:px-8 flex items-center justify-between gap-2">
          <img
            src="https://static.readdy.ai/image/6afa39c0c3f9276f3764fc25ca77885c/b9a66245431c83a5769119df80d4842a.png"
            alt="Filmodeling Logo"
            className={`w-auto transition-all duration-500 ${scrolled ? 'h-10 md:h-20' : 'h-12 md:h-28'}`}
          />
          <button
            onClick={onCtaClick}
            className="whitespace-nowrap bg-gradient-to-r from-yellow-600 to-yellow-400 text-black font-bold text-xs tracking-widest uppercase px-3 md:px-6 py-2 md:py-2.5 rounded-md hover:from-yellow-400 hover:to-yellow-600 transition-all duration-300 cursor-pointer flex-shrink-0"
          >
            <span className="hidden sm:inline">Prenota il Posto</span>
            <span className="sm:hidden">Prenota</span>
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-black">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/22419dc384fca0a064ad7f60c9d33572.jpg"
            alt="Filmodeling Hero Background"
            className="w-full h-full object-cover object-top opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        </div>

        {/* Decorative lines — hidden on mobile */}
        <div className="hidden md:block absolute top-0 left-0 w-px h-full bg-gradient-to-b from-transparent via-yellow-600/30 to-transparent" style={{ left: '10%' }} />
        <div className="hidden md:block absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-yellow-600/20 to-transparent" style={{ right: '15%' }} />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-28 md:pt-24 pb-16 w-full">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 border border-yellow-600/50 text-yellow-500 text-xs tracking-widest uppercase px-3 md:px-4 py-1.5 md:py-2 rounded-full mb-6 md:mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 inline-block" />
              Casting Open Day · 30 Marzo 2026
            </div>

            {/* Title */}
            <h1
              className="text-3xl sm:text-4xl md:text-6xl xl:text-7xl font-bold leading-tight mb-5 md:mb-6"
              style={{ fontFamily: "'Playfair Display', serif", color: '#F5E9C8' }}
            >
              Sogni di entrare{' '}
              <em className="not-italic" style={{ color: '#D4AF37' }}>
                nel mondo
              </em>{' '}
              dello spettacolo?
            </h1>

            <p
              className="text-lg md:text-2xl font-light mb-3 md:mb-4 text-white/90"
              style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.02em' }}
            >
              Il tuo futuro inizia da qui.
            </p>

            <p
              className="text-sm md:text-base text-gray-300 mb-8 md:mb-10 max-w-xl leading-relaxed"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
            >
              Partecipa al Casting Open Day di Filmodeling. Da oltre vent'anni formiamo
              modelli, attrici e professionisti dello spettacolo tra Pozzuoli e Napoli.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                onClick={onCtaClick}
                className="whitespace-nowrap group relative overflow-hidden bg-gradient-to-r from-yellow-700 to-yellow-500 text-black font-bold tracking-widest uppercase px-5 sm:px-8 md:px-10 py-3 md:py-4 rounded-md text-xs md:text-sm transition-all duration-300 cursor-pointer hover:shadow-xl hover:shadow-yellow-700/40 hover:scale-105"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <span className="sm:hidden">Prenota Gratis</span>
                  <span className="hidden sm:inline">Prenota il Tuo Posto Gratis</span>
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </button>
              <a
                href="#chi-siamo"
                className="whitespace-nowrap text-gray-300 text-xs md:text-sm tracking-widest uppercase flex items-center gap-2 hover:text-yellow-400 transition-colors duration-300 cursor-pointer"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Scopri di più
                <i className="ri-arrow-down-line text-base" />
              </a>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-6 md:gap-10 mt-10 md:mt-16 pt-8 md:pt-10 border-t border-white/10">
              {[
                { value: '20+', label: 'Anni di Esperienza' },
                { value: '500+', label: 'Talenti Formati' },
                { value: '4', label: 'Corsi Specialistici' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div
                    className="text-2xl md:text-3xl font-bold"
                    style={{ fontFamily: "'Playfair Display', serif", color: '#D4AF37' }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-xs text-gray-400 tracking-widest uppercase mt-1"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-500">
          <span className="text-xs tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px' }}>Scorri</span>
          <div className="w-px h-10 bg-gradient-to-b from-yellow-600 to-transparent" />
        </div>
      </section>
    </>
  );
}
